import useListInsee, { searchListInsee } from 'hooks/useListInsee'
import { ValueType, OptionTypeBase as OptionsType } from 'react-select'
import AsyncSelect from 'react-select/async'
import TripleGrow from 'components/Loading/TripleGrow'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import Location from 'interfaces/Location'
import './AutocompleteInsee.scss'
import { useCallback } from 'react'

interface Props {
  locations: Location[]
  setLocations: (l: Location[]) => void
}

interface LocationOption extends Location {
  value: string
  label: string
  isDisabled: boolean // Utilisé par la lib pour désactiver l'option
}

const AutocompleteInsee = (props: Props): JSX.Element => {
  const { locations, setLocations } = props
  const { listInsee, error, loading, moreResults } = useListInsee()

  const handleChange = (selectedLocations: ValueType<OptionsType, boolean>) => {
    const cleanLocations = (selectedLocations as LocationOption[]).map(
      ({ codeInsee, nomCommune, codesPostaux }) => ({
        codeInsee,
        nomCommune,
        codesPostaux,
      })
    )
    setLocations(cleanLocations)
  }

  const createLocationOption = ({
    codeInsee,
    nomCommune,
    codesPostaux,
  }: Location) => ({
    value: codeInsee,
    label: `${nomCommune.toLowerCase()} (${codesPostaux.join(', ')})`,
    codeInsee,
    nomCommune,
    codesPostaux,
    isDisabled: false,
  })

  const moreResultsOption = {
    label: 'Trop de résultats, veuillez affiner votre recherche',
    value: '',
    codeInsee: '',
    nomCommune: '',
    codesPostaux: [],
    isDisabled: true,
  }
  const options = listInsee.map(createLocationOption)
  if (moreResults) {
    options.push(moreResultsOption)
  }

  const NoOptionsMessage = useCallback(
    () => (
      <div className="react-select__no-options-message">
        {loading && <TripleGrow />}
        {error && <p className="text-danger">{error}</p>}
        {!loading && !error && <p>Aucune ville disponible</p>}
      </div>
    ),
    [error, loading]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- The lib use 'any' as well...
  const MultiValueRemove = useCallback(
    ({ data }: any) => {
      const handleRemove = () => {
        if (!locations) return
        const newValueArray = locations.filter(
          ({ codeInsee }: { codeInsee: string }) => codeInsee !== data.value
        )
        setLocations(newValueArray)
      }

      return (
        <div
          onClick={handleRemove}
          onKeyPress={handleRemove}
          className="react-select__multi-value__remove"
          role="button"
          tabIndex={0}
        >
          <IoIosCloseCircleOutline />
        </div>
      )
    },
    [locations, setLocations]
  )

  const selectedLocations = locations.map(createLocationOption)

  const searchOptions = (
    inputValue: string,
    callback: (options: LocationOption[]) => void
  ): void => {
    searchListInsee(inputValue).then(
      ({ listInsee: listeInseeResult, moreResults: moreResultsResult }) => {
        const newOptions = listeInseeResult.map(createLocationOption)
        if (moreResultsResult) {
          newOptions.push(moreResultsOption)
        }
        callback(newOptions)
      }
    )
  }

  return (
    <AsyncSelect
      classNamePrefix="react-select"
      isMulti
      placeholder="Choisissez la localisation !"
      value={selectedLocations}
      onChange={handleChange}
      defaultOptions={options}
      loadOptions={searchOptions}
      components={{ NoOptionsMessage, MultiValueRemove }}
    />
  )
}

export default AutocompleteInsee
